/**
 * WHAT SHE AGREED TO, IN WORDS, and the one instant a form is allowed to invent.
 *
 * The consent register is authoritative in ops (`docs/erd.md` Part 8, question 1) and it is
 * append-only: a withdrawal is a second row and never an edit, and the position in force is the
 * last row for the person and the purpose. Until round two of the bug hunt nothing in the product
 * could write one at all, so the answer for every household ops had ever created was no, and every
 * message the office approved was refused after the sentence had been written.
 *
 * The sentences below are the reading half. They are here rather than inside the household screen
 * because a claim about what somebody agreed to is the kind of sentence that has to be tested, and
 * because two screens ask the same question: the household page states the position, and the move
 * flow explains why a message cannot go.
 *
 * NOT KNOWN IS ITS OWN ANSWER, and it is not the same as no. A read that does not carry the
 * register's answer is a read that did not ask, so the line says what the rule is instead of
 * claiming a position nobody has looked up. Guessing "no" would tell somebody to ring a client who
 * agreed months ago; guessing "yes" would send a message she never asked for.
 */
import { CAPTURE_METHODS, type CaptureMethod, type ConsentPurpose } from '@fk/shared';
import type { ConsentCapture, ContactMessaging } from './api.ts';
import { formatDay, orNull } from './format.ts';
import { CAPTURE_METHOD_LABELS } from './labels.ts';

export interface MessagingLine {
  /** True when the register answered at all. False when this read did not carry it. */
  readonly known: boolean;
  /** What may be sent to her, in a sentence a person reads out loud. */
  readonly sentence: string;
  /** How the position in force was recorded, when there is a record of it. */
  readonly evidence: string | null;
}

const UNKNOWN: MessagingLine = Object.freeze({
  known: false,
  sentence: 'They are messaged only where the register says they agreed, and nowhere else.',
  evidence: null,
});

/** The day and the way it was captured, which for a consent given on a call is all there is. */
function evidenceOf(recordedOn: string | null, captureMethod: CaptureMethod | null): string | null {
  const day = formatDay(recordedOn);
  const how = captureMethod === null ? null : CAPTURE_METHOD_LABELS[captureMethod].toLowerCase();
  if (day && how) return `Recorded ${day}, ${how}.`;
  if (day) return `Recorded ${day}.`;
  if (how) return `Recorded ${how}.`;
  return null;
}

export function messagingLine(messaging: ContactMessaging | null | undefined): MessagingLine {
  if (!messaging) return UNKNOWN;
  const { maySms, mayEmail } = messaging;
  const evidence = evidenceOf(messaging.recordedOn, messaging.captureMethod);
  if (maySms && mayEmail) {
    return {
      known: true,
      sentence: 'They may be texted and emailed about their cleans.',
      evidence,
    };
  }
  if (maySms) {
    return {
      known: true,
      sentence: 'They may be texted about their cleans. Nothing goes by email.',
      evidence,
    };
  }
  if (mayEmail) {
    return {
      known: true,
      sentence: 'They may be emailed about their cleans. Nothing goes by text.',
      evidence,
    };
  }
  return {
    known: true,
    sentence:
      evidence === null
        ? 'Nothing may be sent to them, because nobody has recorded what they agreed to.'
        : 'Nothing may be sent to them. Ring them when a visit moves.',
    evidence,
  };
}

/**
 * THE INSTANT A CONSENT ROW CARRIES, from the day somebody typed.
 *
 * `captured_at` is when she actually said it, and `created_at` is when we managed to write it
 * down; only the first of them is evidence. The office is usually typing it while she is still on
 * the phone, so the day it happened is today and the honest instant is now: nothing is invented
 * and the server stamps its own clock. An earlier day is a consent somebody is writing up
 * afterwards, and the instant for that has to land INSIDE that day in Florida.
 *
 * MIDDAY UTC, NOT MIDNIGHT, and that is the whole point of this function. Every day in this
 * product is an America/New_York day, and midnight UTC on a day is eight in the evening on the day
 * BEFORE it here. So a consent written up as given on the 7th, stamped at UTC midnight, is
 * recorded as the 6th: the evidence column the register exists to hold is off by one, and worse,
 * the ordering `consentFor` reads puts that grant behind a withdrawal taken at ten at night on the
 * 6th, so somebody who has agreed goes on being refused and nothing says why. Noon UTC is seven or
 * eight in the morning here whatever the season, which is the typed day in Florida every time, and
 * still hours behind the server's own now for any day before today, so the `not_yet` refusal
 * cannot fire on it.
 *
 * A day after today is refused here rather than sent, because the server's refusal for it is
 * correct but arrives after the form has been filled in.
 */
export function capturedAtFor(day: string, today: string): string | null {
  if (day === '' || day >= today) return null;
  return `${day}T12:00:00.000Z`;
}

/** True for a day somebody typed that has not happened. Consent cannot be recorded ahead of it. */
export function isAheadOfToday(day: string, today: string): boolean {
  return day !== '' && day > today;
}

/* ------------------------------------------------------------------ *
 * What she can be recorded as agreeing to, in one place.
 * ------------------------------------------------------------------ */

/**
 * WHAT THE OFFICE MAY RECORD HER AGREEING TO: emails, and nothing else, since ops #51.
 *
 * The carrier approved the texting campaign on the promise that people opt in to texts one way
 * only, the box on the website quote form, so a yes to texts said on a call, in person or in writing
 * is recorded by nobody here, and the server refuses one (`texts_need_the_website_box`). The box
 * reaches the register by itself, when her lead is booked or when she ticks it again on the form.
 *
 * These labels are written once and read by both screens that ask the question, the household
 * page and the new-client form, because the two have to word it the same way: what a person ticks
 * while somebody is on the phone and what the household page says she agreed to are the same
 * claim, and two spellings of it are two claims that can drift.
 */
export const CONSENT_CHOICES = [
  { id: 'email', label: 'Emails about their cleans', purposes: ['operational_email'] },
] as const satisfies readonly {
  id: string;
  label: string;
  purposes: readonly ConsentPurpose[];
}[];

export type ConsentChoice = (typeof CONSENT_CHOICES)[number]['id'];

/** What every screen that could once record a yes to texts says instead. */
export const TEXT_CONSENT_NOTE =
  'Texts: only the box on the website quote form counts. It is recorded here when their lead is booked, or when they tick it again on the form.';

/**
 * HOW A PERSON AT THE OFFICE HEARD IT: every way but the box on the website form, which only the
 * website records (`booking_form_is_the_websites_word`). The two screens that ask how offer these.
 */
export const OFFICE_CAPTURE_METHODS: readonly CaptureMethod[] = CAPTURE_METHODS.filter(
  (method) => method !== 'booking_form',
);

/** What the new-client form holds while it is being typed. Nothing here is sent on its own. */
export interface ConsentAnswer {
  /** Nobody has asked her unless this is true, and an unasked question is not a refusal. */
  readonly agreed: boolean;
  readonly choice: ConsentChoice;
  readonly captureMethod: CaptureMethod;
  /** Her words, which for a consent given on a call is the only evidence there will ever be. */
  readonly words: string;
}

export const purposesOf = (choice: ConsentChoice): readonly ConsentPurpose[] =>
  CONSENT_CHOICES.find((row) => row.id === choice)?.purposes ?? [];

/**
 * WHAT RIDES ALONG WITH A NEW HOUSEHOLD, or nothing at all.
 *
 * `POST /api/clients` takes the consent inside the contact and writes it in the same audited batch
 * as the person it is about, which is the only moment the two are certainly the same fact. An
 * untouched tick sends nothing rather than a refusal: nobody having asked her is its own answer,
 * and writing `withdrawn` for it would put a position in an append-only register that she never
 * took.
 *
 * NO INSTANT IS INVENTED HERE. The form is typed while she is on the phone, so the moment she said
 * it is now and the server's own clock is the honest stamp. `capturedAt` is left out altogether
 * rather than filled in from a browser whose day may not be Florida's.
 */
export function consentFromAnswer(answer: ConsentAnswer): ConsentCapture | null {
  if (!answer.agreed) return null;
  // Never a yes to texts, whatever the choice says: that is the website box's alone (ops #51).
  const purposes = purposesOf(answer.choice).filter((purpose) => purpose !== 'operational_sms');
  if (purposes.length === 0) return null;
  return {
    purposes,
    kind: 'granted',
    captureMethod: answer.captureMethod,
    evidenceText: orNull(answer.words),
  };
}

/**
 * WHAT A TICK CANNOT BE RECORDED WITHOUT, in sentences somebody can act on.
 *
 * A consent to emails against a household with no address is a promise the product cannot keep: the
 * register says yes, the move flow finds nowhere to send it, and the first anybody hears of it is
 * a visit that moved in silence. So the tick asks for the thing it commits us to, at the moment it
 * is ticked, on the same form the address is typed on. There is no texting clause, because nothing
 * here records a yes to texts (ops #51).
 */
export function consentProblems(answer: ConsentAnswer, email: string): string[] {
  if (!answer.agreed) return [];
  const purposes = purposesOf(answer.choice);
  const problems: string[] = [];
  if (purposes.includes('operational_email') && email.trim() === '') {
    problems.push('They agreed to emails, so the person to ring needs an email address.');
  }
  return problems;
}
